import React, { useState, useEffect } from 'react';
import { canEdit } from '../utils/permissions';
import { partService } from '../services/partService';
import { vehicleService } from '../services/vehicleService';
import { printReport } from '../utils/printUtils'; // 공용 인쇄 유틸 로드

const formatDateTime = (dateObj = new Date()) => {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  const h = String(dateObj.getHours()).padStart(2, '0');
  const min = String(dateObj.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}`;
};

const Part = ({ user }) => {
  const [parts, setParts] = useState([]); 
  const [masterModels, setMasterModels] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModel, setFilterModel] = useState('전체'); 

  // 긴급 재고 패널 전용 독립 필터 상태
  const [alertThreshold, setAlertThreshold] = useState(3); 
  const [alertFilterModel, setAlertFilterModel] = useState('전체'); 

  // 🚀 [신규 추가] 긴급 재고 패널 렌더링 성능 최적화를 위한 전용 페이지네이션 상태
  const [alertCurrentPage, setAlertCurrentPage] = useState(1);
  const alertItemsPerPage = 20; // 알림 패널은 화면이 작으므로 한 페이지에 20개씩 노출

  // 메인 마스터 목록 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; 

  const [receiveModal, setReceiveModal] = useState({ isOpen: false, part: null, addQty: '', note: '' });
  const [historyModal, setHistoryModal] = useState({ isOpen: false, part: null, history: [] });

  const initialForm = { 
    id: null, partNo: '', partName: '', spec: '', modelName: '공용부품', 
    origin: '내수', currency: 'KRW', importCost: 0, purchasePrice: 0, 
    outgoingPrice: 0, consumerPrice: 0, unit: 'EA', stock: 0 
  };
  const [formData, setFormData] = useState(initialForm);

  const refreshData = async () => {
    setLoading(true);
    const partData = await partService.getParts();
    const modelData = await vehicleService.getVehicleModels();
    setParts(partData || []); 
    setMasterModels(modelData || []);
    setLoading(false);
  };

  useEffect(() => { 
    refreshData(); 
    const savedSearchParam = sessionStorage.getItem('E2B_PART_SEARCH_PARAM');
    if (savedSearchParam) {
      setSearchTerm(savedSearchParam); 
      sessionStorage.removeItem('E2B_PART_SEARCH_PARAM'); 
    }
  }, []);

  // 메인 검색어/필터 변경 시 메인 1페이지로 복귀
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterModel]);

  // 🚀 [신규 연동] 긴급 재고 조건이 바뀔 때 알림 패널도 무조건 1페이지로 복귀
  useEffect(() => {
    setAlertCurrentPage(1);
  }, [alertThreshold, alertFilterModel]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!formData.partNo || !formData.partName) return alert("품번과 품명은 필수 입력 사항입니다.");
    setLoading(true);
    try {
      const isDuplicate = await partService.checkDuplicate(formData.partNo, formData.id);
      if (isDuplicate) {
        alert("이미 등록된 품번입니다.");
        setLoading(false);
        return;
      }
      
      const prevStock = formData.id ? parts.find(p => p.id === formData.id)?.stock || 0 : 0;
      
      if (isEditing) {
        await partService.updatePart(formData);
        if (Number(prevStock) !== Number(formData.stock)) {
           await partService.addStockHistory(formData.id, {
             date: formatDateTime(),
             type: '수정',
             qty: Number(formData.stock) > Number(prevStock) ? `+${Number(formData.stock) - Number(prevStock)}` : `${Number(formData.stock) - Number(prevStock)}`,
             prevStock: Number(prevStock),
             currentStock: Number(formData.stock),
             note: '관리자 수동 수정',
             user: user?.name || '관리자'
           });
        }
      } else {
        await partService.addPart(formData);
      }
      
      alert("✅ 저장이 완료되었습니다.");
      setFormData(initialForm); 
      setIsEditing(false); 
      await refreshData();
    } catch (err) { alert("저장 중 에러가 발생했습니다."); }
    setLoading(false);
  };

  const handleEdit = (p) => {
    setFormData({
      ...initialForm,
      ...p,
      partNo: p.partNo || p.partCode || '',
      consumerPrice: p.consumerPrice || p.retailPrice || 0,
      outgoingPrice: p.outgoingPrice || p.supplyPrice || 0,
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleReceiveSubmit = async (e) => {
    e.preventDefault();
    const qty = Number(receiveModal.addQty);
    if (qty <= 0) return alert("추가할 수량은 1 이상이어야 합니다.");
    
    setLoading(true);
    try {
      const prevStock = Number(receiveModal.part.stock || 0);
      const newStock = prevStock + qty;
      
      await partService.updatePart({ ...receiveModal.part, stock: newStock });
      await partService.addStockHistory(receiveModal.part.id, {
        date: formatDateTime(),
        type: '입고',
        qty: `+${qty}`,
        prevStock: prevStock,
        currentStock: newStock,
        note: receiveModal.note || '빠른 입고 처리',
        user: user?.name || '관리자'
      });

      alert(`✅ ${qty}개가 입고 처리되었습니다.\n(현재고: ${newStock}개)`);
      setReceiveModal({ isOpen: false, part: null, addQty: '', note: '' });
      await refreshData();
    } catch (error) { alert("입고 처리 중 에러가 발생했습니다."); }
    setLoading(false);
  };

  const openHistoryModal = async (p) => {
    setLoading(true);
    try {
      const historyData = await partService.getStockHistory(p.id);
      setHistoryModal({ isOpen: true, part: p, history: historyData });
    } catch (error) { alert("이력을 불러오는데 실패했습니다."); }
    setLoading(false);
  };

  // 상단 긴급 부족 재고 리포트 출력 (출력은 페이지 제한 없이 전체 데이터를 내보냅니다)
  const handlePrintUrgentReport = () => {
    const title = "긴급 부족 재고 알림 내역서";
    const headers = ["부품명", "적용차종", "품번(코드)", "현재고", "단위", "상태"];
    const rows = urgentParts.map(p => [
      p.partName || '',
      p.modelName || '',
      p.partNo || p.partCode || '',
      p.stock || 0,
      p.unit || 'EA',
      Number(p.stock || 0) === 0 ? "품절" : "부족"
    ]);
    const metaInfo = {
      "위험 임계값": `${alertThreshold}개 이하`,
      "필터 차종": alertFilterModel
    };
    printReport(title, headers, rows, metaInfo);
  };

  // 하단 메인 마스터 목록 리포트 출력 
  const handlePrintMainStockReport = () => {
    const title = "부품 마스터 재고 현황 내역서";
    const headers = ["품번", "부품명", "적용모델", "규격", "현재고", "매입가", "출고가"];
    const rows = filteredParts.map(p => [
      p.partNo || p.partCode || '',
      p.partName || '',
      p.modelName || '',
      p.spec || '',
      `${p.stock || 0} ${p.unit || 'EA'}`,
      `₩${Number(p.purchasePrice || 0).toLocaleString()}`,
      `₩${Number(p.outgoingPrice || p.supplyPrice || 0).toLocaleString()}`
    ]);
    const metaInfo = {
      "조회 차종": filterModel,
      "검색 조건": searchTerm ? `"${searchTerm}"` : "전체 품목"
    };
    printReport(title, headers, rows, metaInfo);
  };

  // 메인 재고 목록 EXCEL 다운로드
  const handleDownloadMainExcel = () => {
    if (filteredParts.length === 0) {
      alert("다운로드할 재고 데이터가 없습니다.");
      return;
    }
    const headers = ["품번", "부품명", "적용모델", "규격", "재고수량", "단위", "매입가", "출고가"];
    const rows = filteredParts.map(p => [
      `"${String(p.partNo || p.partCode || '')}"`,
      `"${String(p.partName || '').replace(/"/g, '""')}"`,
      `"${String(p.modelName || '')}"`,
      `"${String(p.spec || '')}"`,
      p.stock || 0,
      `"${p.unit || 'EA'}"`,
      p.purchasePrice || 0,
      p.outgoingPrice || p.supplyPrice || 0
    ]);
    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `부품_재고_목록_${formatDateTime().split(' ')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const safeParts = parts || [];
  
  // 1. 긴급 재고 전체 데이터 필터링
  const urgentParts = safeParts.filter(p => {
    const matchStock = Number(p.stock || 0) <= alertThreshold;
    const dbModel = String(p.modelName || '').replace(/\s+/g, '').toLowerCase();
    const selectedModel = String(alertFilterModel || '').replace(/\s+/g, '').toLowerCase();
    return matchStock && (selectedModel === '전체' || dbModel === selectedModel || dbModel.includes(selectedModel));
  });

  // 🚀 2. [신규 추가] 긴급 재고 데이터를 20개씩 슬라이싱 (성능 병목 해결)
  const totalAlertPages = Math.ceil(urgentParts.length / alertItemsPerPage);
  const currentAlertParts = urgentParts.slice((alertCurrentPage - 1) * alertItemsPerPage, alertCurrentPage * alertItemsPerPage);

  // 3. 메인 재고 목록 데이터 필터링 및 슬라이싱
  const filteredParts = safeParts.filter(p => {
    const partNoStr = String(p.partNo || p.partCode || '');
    const partNameStr = String(p.partName || '');
    const matchSearch = partNoStr.toLowerCase().includes(searchTerm.toLowerCase()) || 
                        partNameStr.toLowerCase().includes(searchTerm.toLowerCase());
    const dbModel = String(p.modelName || '').replace(/\s+/g, '').toLowerCase();
    const selectedModel = String(filterModel || '').replace(/\s+/g, '').toLowerCase();
    return matchSearch && (selectedModel === '전체' || dbModel === selectedModel || dbModel.includes(selectedModel));
  });

  const totalPages = Math.ceil(filteredParts.length / itemsPerPage);
  const currentParts = filteredParts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const style = {
    wrapper: { padding: '40px 100px', background: '#f8fafc', minHeight: '100vh' },
    card: { background: 'white', padding: '35px', borderRadius: '20px', boxShadow: '0 4px 25px rgba(0,0,0,0.05)', marginBottom: '30px' },
    input: { width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', boxSizing: 'border-box', outline: 'none' },
    label: { fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '5px', display: 'block' },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '20px' },
    btnPrimary: { padding: '15px 40px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
    btnGhost: { padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', color: '#64748b' },
    scrollArea: { maxHeight: '600px', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '10px' },
    textRight: { textAlign: 'right' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
    modalContent: { background: 'white', padding: '30px', borderRadius: '20px', width: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' },
    
    alertCard: { background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 25px rgba(0,0,0,0.05)', borderTop: '4px solid #ef4444', marginBottom: '30px' },
    alertHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' },
    alertTitle: { fontSize: '18px', fontWeight: 'bold', color: '#dc2626', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },
    alertActions: { display: 'flex', alignItems: 'center', gap: '10px' },
    alertMinInput: { width: '70px', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', textAlign: 'center', fontWeight: 'bold', outline: 'none' },
    alertSelect: { padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white', outline: 'none', cursor: 'pointer', fontWeight: '500' },
    alertPrintBtn: { padding: '6px 15px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    
    mainPrintBtn: { padding: '10px 18px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    mainExcelBtn: { padding: '10px 18px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },

    alertScroll: { maxHeight: '280px', overflowY: 'auto', paddingRight: '5px' }, // 높이를 살짝 키워 페이지네이션 포함 시 답답함 해소
    alertItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
    badgeSoldOut: { background: '#fee2e2', color: '#ef4444', padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', marginLeft: '8px' },
    badgeWarning: { background: '#fff7ed', color: '#ea580c', padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', marginLeft: '8px' }
  };

  return (
    <div style={style.wrapper}>
      {/* 빠른 입고 모달 */}
      {receiveModal.isOpen && (
        <div style={style.modalOverlay} onClick={() => setReceiveModal({ ...receiveModal, isOpen: false })}>
          <div style={style.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, borderBottom: '2px solid #10b981', paddingBottom: '10px', color: '#10b981' }}>📥 부품 빠른 입고</h3>
            <form onSubmit={handleReceiveSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={style.label}>추가 입고 수량 *</label>
                <input type="number" required style={{...style.input, border: '2px solid #10b981'}} placeholder="예: 50" value={receiveModal.addQty} onChange={e => setReceiveModal({...receiveModal, addQty: e.target.value})} autoFocus />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={style.label}>입고 사유 / 메모</label>
                <input style={style.input} placeholder="예: 정기 발주분 입고" value={receiveModal.note} onChange={e => setReceiveModal({...receiveModal, note: e.target.value})} />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setReceiveModal({ ...receiveModal, isOpen: false })} style={{...style.btnGhost, flex: 1}}>취소</button>
                <button type="submit" disabled={loading} style={{...style.btnPrimary, background: '#10b981', flex: 1}}>입고 처리</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 부품 입출고 이력 확인 모달 (🔥 누락 구문 긴급 추가 복구 완료) */}
      {historyModal.isOpen && (
        <div style={style.modalOverlay} onClick={() => setHistoryModal({ ...historyModal, isOpen: false })}>
          <div style={{...style.modalContent, width: '650px'}} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, borderBottom: '2px solid #8b5cf6', paddingBottom: '10px', color: '#7c3aed' }}>
              📜 [이력] {historyModal.part?.partName || '부품'} ({historyModal.part?.partNo})
            </h3>
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                    <th style={{ padding: '10px' }}>일시</th>
                    <th style={{ padding: '10px' }}>구분</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>변동량</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>최종재고</th>
                    <th style={{ padding: '10px' }}>담당자</th>
                    <th style={{ padding: '10px' }}>비고(사유)</th>
                  </tr>
                </thead>
                <tbody>
                  {historyModal.history && historyModal.history.length > 0 ? (
                    historyModal.history.map((h, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px', color: '#64748b' }}>{h.date}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{
                            padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold',
                            background: h.type === '입고' ? '#ecfdf5' : h.type === '출고' ? '#fee2e2' : '#f1f5f9',
                            color: h.type === '입고' ? '#059669' : h.type === '출고' ? '#ef4444' : '#475569'
                          }}>{h.type}</span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: String(h.qty).includes('+') ? '#059669' : '#ef4444' }}>{h.qty}</td>
                        <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>{h.currentStock}</td>
                        <td style={{ padding: '10px', color: '#475569' }}>{h.user || '시스템'}</td>
                        <td style={{ padding: '10px', color: '#64748b', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={h.note}>{h.note || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>기록된 입출고 변동 이력이 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setHistoryModal({ isOpen: false, part: null, history: [] })} style={{...style.btnPrimary, background: '#64748b', padding: '10px 25px'}}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 상단 긴급 재고 알림 패널 */}
      <div style={style.alertCard}>
        <div style={style.alertHeader}>
          <h3 style={style.alertTitle}>⚠️ 긴급 재고 알림 ({urgentParts.length}건)</h3>
          <div style={style.alertActions}>
            <span style={{ fontSize: '13px', fontWeight: 'bold' }}>기준 수량:</span>
            <input type="number" style={style.alertMinInput} value={alertThreshold} min="0" onChange={e => setAlertThreshold(Number(e.target.value))} />
            <span>개 이하</span>
            <select style={style.alertSelect} value={alertFilterModel} onChange={e => setAlertFilterModel(e.target.value)}>
              <option value="전체">전체 차종</option>
              <option value="공용부품">공용부품</option>
              {masterModels.map(m => <option key={m.id} value={m.modelName}>{m.modelName}</option>)}
            </select>
            <button style={style.alertPrintBtn} onClick={handlePrintUrgentReport}>출력</button>
          </div>
        </div>
        <div style={style.alertScroll}>
          {/* 🚀 전체 데이터를 돌리지 않고 분할된 currentAlertParts만 렌더링 */}
          {currentAlertParts.map(p => (
            <div key={p.id} style={style.alertItem}>
              <div><b>{p.partName}</b><br/><span style={{ fontSize: '12px', color: '#64748b' }}>{p.modelName} | {p.partNo}</span></div>
              <div><span style={{ color: '#ef4444', fontWeight: 'bold' }}>{p.stock} {p.unit}</span></div>
            </div>
          ))}
          
          {urgentParts.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
              조건에 해당하는 긴급 부족 재고가 없습니다.
            </div>
          )}
        </div>
        
        {/* 🚀 긴급 재고 전용 미니 페이지네이션 컨트롤바 */}
        {totalAlertPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '15px', marginTop: '10px', gap: '10px', borderTop: '1px dashed #e2e8f0' }}>
            <button
              onClick={() => setAlertCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={alertCurrentPage === 1}
              style={{ padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', background: alertCurrentPage === 1 ? '#f8fafc' : 'white', cursor: alertCurrentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '12px', color: '#475569', fontWeight: 'bold' }}
            >
              ◀ 이전
            </button>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#dc2626' }}>
              {alertCurrentPage} <span style={{ color: '#94a3b8' }}>/ {totalAlertPages}</span>
            </span>
            <button
              onClick={() => setAlertCurrentPage(prev => Math.min(prev + 1, totalAlertPages))}
              disabled={alertCurrentPage === totalAlertPages}
              style={{ padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', background: alertCurrentPage === totalAlertPages ? '#f8fafc' : 'white', cursor: alertCurrentPage === totalAlertPages ? 'not-allowed' : 'pointer', fontSize: '12px', color: '#475569', fontWeight: 'bold' }}
            >
              다음 ▶
            </button>
          </div>
        )}
      </div>

      {/* 부품 등록 폼 */}
      <div style={style.card}>
        <h2 style={{ margin: '0 0 25px 0' }}>⚙️ 부품 마스터 관리 (Cloud)</h2>
        <form onSubmit={onSave}>
          <div style={style.grid4}>
            <div><label style={style.label}>1. 품번 *</label><input style={style.input} value={formData.partNo} onChange={e=>setFormData({...formData, partNo:e.target.value})} /></div>
            <div><label style={style.label}>2. 품명 *</label><input style={style.input} value={formData.partName} onChange={e=>setFormData({...formData, partName:e.target.value})} /></div>
            <div><label style={style.label}>3. 규격(Spec)</label><input style={style.input} value={formData.spec} onChange={e=>setFormData({...formData, spec:e.target.value})} /></div>
            <div><label style={style.label}>4. 적용 모델</label>
              <select style={style.input} value={formData.modelName} onChange={e=>setFormData({...formData, modelName:e.target.value})}>
                <option value="공용부품">공용부품</option>
                {masterModels.map(m => <option key={m.id} value={m.modelName}>{m.modelName}</option>)}
              </select>
            </div>
          </div>
          <div style={style.grid4}>
            <div><label style={style.label}>5. 원산지</label><input style={style.input} value={formData.origin} onChange={e=>setFormData({...formData, origin:e.target.value})} /></div>
            <div><label style={style.label}>6. 통화</label>
              <select style={style.input} value={formData.currency} onChange={e=>setFormData({...formData, currency:e.target.value})}>
                <option value="KRW">KRW (원)</option><option value="CNY">CNY (위안)</option>
              </select>
            </div>
            <div><label style={style.label}>7. 수입원가</label><input type="number" style={style.input} value={formData.importCost} onChange={e=>setFormData({...formData, importCost:Number(e.target.value)})} /></div>
            <div><label style={style.label}>8. 매입가</label><input type="number" style={style.input} value={formData.purchasePrice} onChange={e=>setFormData({...formData, purchasePrice:Number(e.target.value)})} /></div>
          </div>
          <div style={style.grid4}>
            <div><label style={style.label}>9. 출고가</label><input type="number" style={style.input} value={formData.outgoingPrice} onChange={e=>setFormData({...formData, outgoingPrice:Number(e.target.value)})} /></div>
            <div><label style={style.label}>10. 소비자가</label><input type="number" style={style.input} value={formData.consumerPrice} onChange={e=>setFormData({...formData, consumerPrice:Number(e.target.value)})} /></div>
            <div><label style={style.label}>11. 단위</label><input style={style.input} value={formData.unit} onChange={e=>setFormData({...formData, unit:e.target.value})} /></div>
            <div><label style={style.label}>12. 현재 재고량</label><input type="number" style={style.input} value={formData.stock} onChange={e=>setFormData({...formData, stock:Number(e.target.value)})} /></div>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
            {isEditing && <button type="button" onClick={()=>{setFormData(initialForm); setIsEditing(false);}} style={style.btnGhost}>취소</button>}
            {canEdit() && <button type="submit" disabled={loading} style={style.btnPrimary}>{isEditing ? "부품 수정 완료" : "부품 정보 저장"}</button>}
          </div>
        </form>
      </div>

      {/* 부품 재고 목록 테이블 */}
      <div style={style.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
          <h3>📦 부품 재고 목록 <span style={{fontSize: '14px', color: '#ef4444'}}>(총 {filteredParts.length}건 로딩됨)</span></h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button style={style.mainPrintBtn} onClick={handlePrintMainStockReport}>🖨️ 출력하기</button>
            <button style={style.mainExcelBtn} onClick={handleDownloadMainExcel}>📊 EXCEL 다운로드</button>
            <select style={{...style.input, width: '150px', border: '2px solid #3b82f6'}} value={filterModel} onChange={e => setFilterModel(e.target.value)}>
              <option value="전체">전체 차종</option>
              <option value="공용부품">공용부품</option>
              {masterModels.map(m => <option key={m.id} value={m.modelName}>{m.modelName}</option>)}
            </select>
            <input style={{ padding: '10px', width: '250px', borderRadius: '8px', border: '1px solid #ddd' }} placeholder="품번 또는 품명 검색..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
        </div>
        
        <div style={style.scrollArea}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10 }}>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '13px' }}>
                <th style={{ padding: '15px' }}>품번</th><th>부품명 / 모델</th><th style={{ padding: '15px', ...style.textRight }}>재고</th><th style={{ padding: '15px', ...style.textRight }}>매입가</th><th style={{ padding: '15px', ...style.textRight }}>출고가</th>
                <th style={{ padding: '15px', textAlign: 'center', minWidth: '240px' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {currentParts.map(p => (
                <tr style={{ borderBottom: '1px solid #f1f5f9' }} key={p.id}>
                  <td style={{ padding: '15px' }}><b>{p.partNo || p.partCode}</b></td>
                  <td>{p.partName}<br/><span style={{ fontSize: '12px', color: '#94a3b8' }}>{p.modelName} | {p.spec}</span></td>
                  <td style={{ padding: '15px', ...style.textRight, fontWeight: 'bold' }}>{Number(p.stock || 0).toLocaleString()} {p.unit}</td>
                  <td style={{ padding: '15px', ...style.textRight }}>₩{Number(p.purchasePrice || 0).toLocaleString()}</td>
                  <td style={{ padding: '15px', ...style.textRight }}>₩{Number(p.outgoingPrice || 0).toLocaleString()}</td>
                  
                  <td style={{ padding: '15px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                      <button onClick={() => setReceiveModal({ isOpen: true, part: p, addQty: '', note: '' })} style={{ border: '1px solid #10b981', background: '#ecfdf5', color: '#059669', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>+ 입고</button>
                      <button onClick={() => openHistoryModal(p)} style={{ border: '1px solid #8b5cf6', background: '#f5f3ff', color: '#7c3aed', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>이력</button>
                      <button onClick={() => handleEdit(p)} style={{ border: '1px solid #cbd5e1', background: 'white', color: '#475569', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer' }}>수정</button>
                      {canEdit() && <button onClick={async () => { if(window.confirm('삭제할까요?')) { await partService.deletePart(p.id); refreshData(); } }} style={{ border: '1px solid #fecaca', background: 'white', color: '#ef4444', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer' }}>삭제</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {totalPages > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', gap: '15px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{ padding: '8px 15px', border: '1px solid #cbd5e1', borderRadius: '8px', background: currentPage === 1 ? '#f8fafc' : 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: '#475569', fontWeight: 'bold' }}
              >
                ◀ 이전
              </button>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#3b82f6' }}>
                {currentPage} <span style={{ color: '#94a3b8' }}>/ {totalPages} 페이지</span>
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{ padding: '8px 15px', border: '1px solid #cbd5e1', borderRadius: '8px', background: currentPage === totalPages ? '#f8fafc' : 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: '#475569', fontWeight: 'bold' }}
              >
                다음 ▶
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Part;